import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useQuery from "Hooks/query.hook";
import useSearch from "Hooks/search.hook";
import { DataTable } from "@life-line-lab/components-react";
import { Col, Divider, Row, Spin } from "antd";
import DataTableFilter from "Components/shared/data-table-filter";
import ReportFilters from "./report-filters";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";
import { LoadingOutlined } from "@ant-design/icons";
import { routeToTrolleyView } from "Helpers/index";
import { useHistory, useLocation } from "react-router-dom";

const TrolleyReportList = ({
  onFilter,
  onDropdownFilter,
  reportListing,
  handleDownloadExcelFile,
  itemPerPage,
  isLoading,
  handleRefreshList,
  isExcelLoading,
}) => {
  const query = useQuery();
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const page: any = query.get("page") || 1;

  const [filterState, setFilterState] = useState<any>({
    status: "",
    sortByCreatedAt: "DESC",
  });
  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const tabKeyPaginationProps = {
    pathWithoutPage: {
      withoutTabKey: `/parcels-order-processing/reporting`,
    },
    pathWithPage: {
      withoutTabKey: `/parcels-order-processing/reporting?page=${page}`,
    },
  };

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.trolleyID").toLocaleUpperCase(),
      dataIndex: ["trolleyParcelData", "trolleyCode"],
      render: (trolleyCode: string, record: any) =>
        trolleyCode ? (
          <a
            className="clickable-id"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              routeToTrolleyView(record.trolleyParcelID, location, history);
            }}
          >
            {trolleyCode}
          </a>
        ) : (
          <span>{"-"}</span>
        ),
    },

    {
      title: t("label.trolleyType").toLocaleUpperCase(),
      dataIndex: ["trolleyData", "trolleyType"],
    },
    {
      title: t("label.loadDate").toLocaleUpperCase(),
      dataIndex: "loadDate",
      render: (_: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            {record?.loadDateTime ? (
              <Col span={24}>{`${moment(record?.data?.loadDateTime).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.loadDateTime).format(
                "hh:mm a",
              )}`}</Col>
            ) : (
              <Col span={24}>-</Col>
            )}
          </Row>
        );
      },
    },
    {
      title: t("label.loadAt").toLocaleUpperCase(),
      dataIndex: ["loadAtData", "locationName"],
    },
    {
      title: t("label.unloadDate").toLocaleUpperCase(),
      dataIndex: "unloadDate",
      render: (_: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            {record?.unloadDateTime ? (
              <Col span={24}>{`${moment(record?.data?.unloadDateTime).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.unloadDateTime).format(
                "hh:mm a",
              )}`}</Col>
            ) : (
              <Col span={24}>-</Col>
            )}
          </Row>
        );
      },
    },
    {
      title: t("label.unloadAt").toLocaleUpperCase(),
      dataIndex: ["unloadAtData", "locationName"],
    },
    {
      title: t("label.lastKnownLocation").toLocaleUpperCase(),
      dataIndex: ["lastKnownLocationData", "locationName"],
    },
    {
      title: t("label.noOfParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcel",
    },
    {
      title: t("label.noOfBoxes").toLocaleUpperCase(),
      dataIndex: "noOfBoxes",
    },
    {
      title: t("label.noOfLoose").toLocaleUpperCase(),
      dataIndex: "noOfLooseParcels",
    },
    {
      title: t("label.noOfBulk").toLocaleUpperCase(),
      dataIndex: "noOfBulkParcels",
    },
    {
      title: t("label.totalWeight").toLocaleUpperCase(),
      dataIndex: "totalWeight",
    },
    {
      title: t("label.totalVolume").toLocaleUpperCase(),
      dataIndex: "totalVolume",
    },
    {
      title: t("label.weightLoadFactor").toLocaleUpperCase(),
      dataIndex: "weightLoadFactor",
    },
    {
      title: t("label.volLoadFactor").toLocaleUpperCase(),
      dataIndex: "volumeLoadFactor",
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
  ];

  const statusOptions = [
    { label: t("option.all"), value: "" },
    { label: "Loading", value: "LOADING" },
    { label: "Loaded", value: "LOADED" },
    { label: "Dispatched", value: "DISPATCHED" },
    { label: "Boarded", value: "BOARDED" },
    { label: "Disembarked", value: "DISEMBARKED" },
    { label: "Received", value: "RECEIVED" },
    { label: "On Hold", value: "ON_HOLD" },
    { label: "Unloaded", value: "UNLOADED" },
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Complete", value: "COMPLETE" },
    { label: "Open", value: "OPEN" },
    { label: "Closed", value: "CLOSED" },
  ];

  const sortByCreatedAt = [
    { label: t("option.latest"), value: "DESC" },
    { label: t("option.earliest"), value: "ASC" },
  ];

  const handleFilterChange = (value: any, name: string) => {
    let selectedValue;

    switch (name) {
      case "dateFrom":
        selectedValue = value ? value.startOf("day").toISOString() : null;
        break;
      case "dateTo":
        selectedValue = value ? value.endOf("day").toISOString() : null;
        break;
      case "onlyLatestStatus":
        selectedValue = value ? "true" : "false"; // boolean will be cleared by the hook
        break;
      default:
        selectedValue = value;
    }

    if (Array.isArray(value)) {
      selectedValue = value.filter((item) => item !== "all");
    }
    setFilterState({ ...filterState, [name]: selectedValue });
  };

  return (
    <div>
      <div className="dm-report-sort-container">
        <ReportFilters
          handleFilterChange={handleFilterChange}
          filterState={filterState}
          handleDownloadExcelFile={handleDownloadExcelFile}
          excelType={"trolley"}
          reportListing={reportListing}
          statusOptions={statusOptions}
          sortByCreatedAt={sortByCreatedAt}
          handleRefreshList={handleRefreshList}
          isLoading={isLoading}
          isExcelLoading={isExcelLoading}
        />
        <Divider />
        <DataTableFilter
          searchPlaceholder={t("label.searchReportingTrolley")}
          onSearch={(text: string) => handleSearch(text)}
        />
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
          <DataTable
            rowKey={(record: any) => record._id}
            columns={columns}
            scroll={{ x: "max-content" }}
            dataSource={reportListing}
          />
      )}
    </div>
  );
};

export default TrolleyReportList;
